import {
  BarcodeStatus,
  ForgeActivityItem,
  ForgeFDClassificationPayload,
  FDDetection,
  ForgeLabeler,
  ForgeLeaderLabeler,
  ForgePriorityClass,
  ForgeQueue,
  ForgeQueueHealth,
  ForgeStatCard,
  ForgeTask,
  ForgeDesignCuratorPayload,
  ForgeDesignReviewPayload,
  ForgeUncertainClassificationPayload,
  ReviewHighlight,
  CuratorCrop,
  MasterUPCData,
  UncertainCrop,
} from '../types';
import { MockProduct, PRODUCTS, productCropUrl, productImageUrl } from './products';

// ===== Chains =====
export const CHAINS = [
  { chainId: 1, name: 'morrisons' },
  { chainId: 2, name: 'asda' },
  { chainId: 3, name: 'lidl_gb' },
  { chainId: 4, name: 'lidl_fr' },
  { chainId: 5, name: 'village' },
] as const;

// ===== Queue definitions =====
const QUEUE_DEFS = [
  { queueName: 'design_review', displayName: 'Design Review' },
  { queueName: 'design_curator', displayName: 'Design Curator' },
  { queueName: 'uncertain_classification', displayName: 'Uncertain Classification' },
  { queueName: 'fd_classification', displayName: 'FD Classification' },
];

export const QUEUE_ORDER: Record<string, number> = Object.fromEntries(
  QUEUE_DEFS.map((q, i) => [q.queueName, i])
);

// Seeded pseudo-random for stable mock data
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const rand = seededRandom(42);

function randomHealth(): ForgeQueueHealth {
  const r = rand();
  if (r < 0.7) return 'healthy';
  if (r < 0.9) return 'attention';
  return 'critical';
}

function randomPriority(): ForgePriorityClass {
  const r = rand();
  if (r < 0.33) return 'high';
  if (r < 0.66) return 'normal';
  return 'low';
}

export const MOCK_QUEUES: ForgeQueue[] = QUEUE_DEFS.flatMap((q) =>
  CHAINS.map((chain) => ({
    queueName: q.queueName,
    displayName: q.displayName,
    chainId: chain.chainId,
    chainName: chain.name,
    taskCount: Math.floor(rand() * 300) + 10,
    claimedCount: Math.floor(rand() * 60),
    priorityClass: randomPriority(),
    health: randomHealth(),
  }))
);

// ===== Admin Stats =====
export const MOCK_ADMIN_STATS: ForgeStatCard[] = [
  { label: 'Team Tasks Today', value: 680, format: 'number', diff: 85, diffLabel: 'vs yesterday', accent: true },
  { label: 'Active Labelers', value: 5, format: 'number' },
  { label: 'Team Accuracy', value: 93.2, format: 'percent', diff: 0.8, diffLabel: 'vs last week' },
  { label: 'Avg Drain Time', value: 2.4, format: 'time' },
];

// ===== Leaderboard =====
export const MOCK_LEADERBOARD: ForgeLeaderLabeler[] = [
  { id: 'l1', name: 'cogito_1', tasksToday: 142, accuracy: 0.97, status: 'online' },
  { id: 'l2', name: 'cogito_2', tasksToday: 112, accuracy: 0.93, status: 'online' },
  { id: 'l3', name: 'cogito_3', tasksToday: 98, accuracy: 0.94, status: 'online' },
  { id: 'l4', name: 'cogito_4', tasksToday: 88, accuracy: 0.91, status: 'online' },
  { id: 'l5', name: 'cogito_5', tasksToday: 76, accuracy: 0.92, status: 'idle' },
];

// ===== Activity Feed =====
export const MOCK_ACTIVITY: ForgeActivityItem[] = [
  { id: 'a1', type: 'submit', description: 'Assigned crop to design', entityId: 'var-0012', timestamp: '2026-03-03T11:42:00Z' },
  { id: 'a2', type: 'merge', description: 'Merged design into', entityId: 'var-0008', timestamp: '2026-03-03T11:38:00Z' },
  { id: 'a3', type: 'create_design', description: 'Created new design', entityId: 'var-0041', timestamp: '2026-03-03T11:35:00Z' },
  { id: 'a4', type: 'skip', description: 'Skipped duplicate cleanup task', entityId: 'task-0019', timestamp: '2026-03-03T11:32:00Z' },
  { id: 'a5', type: 'submit', description: 'Approved pending crop', entityId: 'crop-00142', timestamp: '2026-03-03T11:28:00Z' },
];

// ===== Product helpers =====
const P = PRODUCTS;

function makeSamples(productKey: string, count: number) {
  const p = P[productKey];
  return Array.from({ length: count }, (_, i) => ({
    id: `crop-${productKey}-${i + 1}`,
    imageUrl: productCropUrl(p, i),
  }));
}

// ===== Mock Tasks (for task player) =====

// FD Classification — False Detection pipeline review
function fdClassificationPayload(): ForgeFDClassificationPayload {
  const detections: FDDetection[] = [
    {
      id: 'fd-001',
      cropImageUrl: productCropUrl(P['coca-cola'], 0),
      upc: P['coca-cola'].upc,
      productName: 'Coca-Cola 330ml',
      assignedDesignId: 'd6bb0cd5-d73f',
      assignedDesignImageUrl: productImageUrl(P['coca-cola']),
      fdScore: 0.92,
      designColor: '#DC2626',
      detectionSteps: [
        { step: 'PR Detection', result: 'Coca-Cola 330ml (top-1)', confidence: 0.94, status: 'pass' },
        { step: 'Barcode Match', result: 'UPC 5449000000996 — matched', confidence: 0.88, status: 'pass' },
        { step: 'Design Assign', result: 'Design d6bb0cd5 (main)', confidence: 0.91, status: 'pass' },
        { step: 'FD Check', result: 'Flagged: crop looks like Pepsi side view', confidence: 0.92, status: 'fail' },
      ],
    },
    {
      id: 'fd-002',
      cropImageUrl: productCropUrl(P['pepsi'], 1),
      upc: P['pepsi'].upc,
      productName: 'Pepsi 500ml',
      assignedDesignId: '7c4e9f12-2a85',
      assignedDesignImageUrl: productImageUrl(P['pepsi']),
      fdScore: 0.85,
      designColor: '#1E40AF',
      detectionSteps: [
        { step: 'PR Detection', result: 'Pepsi 500ml (top-1)', confidence: 0.71, status: 'warning' },
        { step: 'Barcode Match', result: 'UPC 4060800001065 — no match', confidence: 0.0, status: 'fail' },
        { step: 'Design Assign', result: 'Design 7c4e9f12 (fallback)', confidence: 0.65, status: 'warning' },
        { step: 'FD Check', result: 'Flagged: barcode mismatch + low PR confidence', confidence: 0.85, status: 'fail' },
      ],
    },
    {
      id: 'fd-003',
      cropImageUrl: productCropUrl(P['red-bull'], 0),
      upc: P['red-bull'].upc,
      productName: 'Red Bull 250ml',
      assignedDesignId: '2b8f4a6e-d1c7',
      assignedDesignImageUrl: productImageUrl(P['red-bull']),
      fdScore: 0.78,
      designColor: '#1D4ED8',
      detectionSteps: [
        { step: 'PR Detection', result: 'Red Bull 250ml (top-1)', confidence: 0.89, status: 'pass' },
        { step: 'Barcode Match', result: 'UPC 9002490100070 — matched', confidence: 0.95, status: 'pass' },
        { step: 'Design Assign', result: 'Design 2b8f4a6e (main)', confidence: 0.87, status: 'pass' },
        { step: 'FD Check', result: 'Flagged: duplicate crop in different store', confidence: 0.78, status: 'warning' },
      ],
    },
    {
      id: 'fd-004',
      cropImageUrl: productCropUrl(P['dr-pepper'], 0),
      upc: P['dr-pepper'].upc,
      productName: 'Dr Pepper 330ml',
      assignedDesignId: undefined,
      assignedDesignImageUrl: undefined,
      fdScore: 0.95,
      designColor: '#7C3AED',
      detectionSteps: [
        { step: 'PR Detection', result: 'Dr Pepper 330ml (top-1)', confidence: 0.42, status: 'fail' },
        { step: 'Barcode Match', result: 'No barcode detected', confidence: 0.0, status: 'fail' },
        { step: 'Design Assign', result: 'No design — unassigned', confidence: 0.0, status: 'fail' },
        { step: 'FD Check', result: 'Flagged: likely non-product crop (shelf label?)', confidence: 0.95, status: 'fail' },
      ],
    },
    {
      id: 'fd-005',
      cropImageUrl: productCropUrl(P['pepsi-max'], 0),
      upc: P['pepsi-max'].upc,
      productName: 'Pepsi Max 330ml',
      assignedDesignId: '7c4e9f12-2a85',
      assignedDesignImageUrl: productImageUrl(P['pepsi']),
      fdScore: 0.88,
      designColor: '#1E40AF',
      detectionSteps: [
        { step: 'PR Detection', result: 'Pepsi 500ml (top-1), Pepsi Max 330ml (top-2)', confidence: 0.76, status: 'warning' },
        { step: 'Barcode Match', result: 'UPC matched to Pepsi 500ml', confidence: 0.82, status: 'pass' },
        { step: 'Design Assign', result: 'Design 7c4e9f12 (Pepsi main — wrong UPC?)', confidence: 0.68, status: 'warning' },
        { step: 'FD Check', result: 'Flagged: design UPC mismatch (Pepsi vs Pepsi Max)', confidence: 0.88, status: 'fail' },
      ],
    },
  ];

  // Reuse candidate designs from uncertain classification mock
  const candidateDesigns = [
    { id: 'var-cola-main', upc: P['coca-cola'].upc, representativeImageUrl: productImageUrl(P['coca-cola']), similarity: 0.92, cropCount: 28, barcodeStatus: 'confirmed' as const, sampleCrops: [{ id: 'sc-1', imageUrl: productCropUrl(P['coca-cola'], 0) }] },
    { id: 'var-pepsi-main', upc: P['pepsi'].upc, representativeImageUrl: productImageUrl(P['pepsi']), similarity: 0.88, cropCount: 22, barcodeStatus: 'pending' as const, sampleCrops: [{ id: 'sc-2', imageUrl: productCropUrl(P['pepsi'], 0) }] },
    { id: 'var-pepsi-max', upc: P['pepsi-max'].upc, representativeImageUrl: productImageUrl(P['pepsi-max']), similarity: 0.85, cropCount: 15, barcodeStatus: 'unconfirmed' as const, sampleCrops: [{ id: 'sc-3', imageUrl: productCropUrl(P['pepsi-max'], 0) }] },
    { id: 'var-rb-main', upc: P['red-bull'].upc, representativeImageUrl: productImageUrl(P['red-bull']), similarity: 0.81, cropCount: 19, barcodeStatus: 'confirmed' as const, sampleCrops: [{ id: 'sc-4', imageUrl: productCropUrl(P['red-bull'], 0) }] },
    { id: 'var-dr-pepper', upc: P['dr-pepper'].upc, representativeImageUrl: productImageUrl(P['dr-pepper']), similarity: 0.79, cropCount: 12, barcodeStatus: 'unconfirmed' as const, sampleCrops: [{ id: 'sc-5', imageUrl: productCropUrl(P['dr-pepper'], 0) }] },
  ];

  return { detections, candidateDesigns };
}

// Design Curator — Red Bull with store-grouped data
// Design colors for triangles
const VC = {
  redBull: '#1D4ED8',   // Blue — correct design
  monster: '#059669',   // Green — wrong design (Monster)
  lucozade: '#D97706',  // Amber — wrong design (Lucozade)
  none: '#6B7280',      // Grey — no design assigned
};

function curatorCrop(product: MockProduct, idx: number, storeId: string, source: string, similarity: number, designColor?: string): CuratorCrop {
  return {
    id: `cur-${product.key}-${storeId}-${idx}`,
    imageUrl: productCropUrl(product, idx),
    similarity,
    storeId,
    source,
    designColor,
  };
}

const CURATOR_SOURCES = ['shelf_scan', 'planogram_v3', 'auto_detect_v2', 'restock_photo'] as const;

/** Generate `count` candidate crops with similarity uniformly spread from 0.99 down to 0.80 */
function candidateSpread(product: MockProduct, storeId: string, count: number): CuratorCrop[] {
  return Array.from({ length: count }, (_, i) => {
    const similarity = +(0.99 - (i / Math.max(count - 1, 1)) * 0.19).toFixed(2);
    return curatorCrop(product, 10 + i, storeId, CURATOR_SOURCES[i % CURATOR_SOURCES.length], similarity);
  });
}

function designCuratorPayload(): ForgeDesignCuratorPayload {
  return {
    designId: '2b8f4a6e-d1c7-43e9-a590-8e3d2f01',
    upc: P['red-bull'].upc,
    barcode: '9002490100070',
    representativeImageUrl: productImageUrl(P['red-bull']),
    targetCropCount: 15,
    stores: [
      // Stores with existing crops (sorted by count desc)
      {
        storeId: 'store-101',
        storeName: 'Lidl Watford',
        existingCrops: [
          curatorCrop(P['red-bull'], 0, 'store-101', 'planogram_v3', 0.97, VC.redBull),
          curatorCrop(P['red-bull'], 1, 'store-101', 'planogram_v3', 0.95, VC.redBull),
          curatorCrop(P['red-bull'], 2, 'store-101', 'shelf_scan', 0.93, VC.redBull),
          curatorCrop(P['monster'], 0, 'store-101', 'auto_detect_v2', 0.41, VC.monster),   // WRONG: Monster in Red Bull design
          curatorCrop(P['lucozade'], 0, 'store-101', 'auto_detect_v2', 0.38, VC.lucozade), // WRONG: Lucozade in Red Bull design
        ],
        candidateCrops: candidateSpread(P['red-bull'], 'store-101', 10),
      },
      {
        storeId: 'store-205',
        storeName: 'Lidl Leeds',
        existingCrops: [
          curatorCrop(P['red-bull'], 0, 'store-205', 'shelf_scan', 0.96, VC.redBull),
          curatorCrop(P['red-bull'], 1, 'store-205', 'planogram_v3', 0.94, VC.redBull),
          curatorCrop(P['trash-blurry'], 0, 'store-205', 'auto_detect_v2', 0.33, VC.none), // TRASH: blurry shelf photo
        ],
        candidateCrops: candidateSpread(P['red-bull'], 'store-205', 10),
      },
      {
        storeId: 'store-310',
        storeName: 'Lidl Bristol',
        existingCrops: [
          curatorCrop(P['red-bull'], 0, 'store-310', 'planogram_v3', 0.95, VC.redBull),
        ],
        candidateCrops: candidateSpread(P['red-bull'], 'store-310', 10),
      },
      // New stores (no existing crops) — prefetched candidates
      {
        storeId: 'store-412',
        storeName: 'Lidl Manchester',
        existingCrops: [],
        candidateCrops: candidateSpread(P['red-bull'], 'store-412', 10),
      },
      {
        storeId: 'store-520',
        storeName: 'Lidl Edinburgh',
        existingCrops: [],
        candidateCrops: candidateSpread(P['red-bull'], 'store-520', 10),
      },
      {
        storeId: 'store-610',
        storeName: 'Lidl Glasgow',
        existingCrops: [],
        candidateCrops: candidateSpread(P['red-bull'], 'store-610', 10),
      },
      {
        storeId: 'store-715',
        storeName: 'Lidl Liverpool',
        existingCrops: [],
        candidateCrops: candidateSpread(P['red-bull'], 'store-715', 10),
      },
      {
        storeId: 'store-820',
        storeName: 'Lidl Cardiff',
        existingCrops: [],
        candidateCrops: candidateSpread(P['red-bull'], 'store-820', 10),
      },
    ],
    similarDesigns: [
      {
        id: 'e4c19a38-7d52-4f8b-b316-2af9d0e1',
        representativeImageUrl: productImageUrl(P['red-bull'], 'Sugar Free'),
        similarity: 0.94,
        cropCount: 8,
        barcode: '9002490100087',
        barcodeStatus: 'confirmed' as BarcodeStatus,
        sampleCrops: [
          curatorCrop(P['red-bull'], 3, 'store-101', 'shelf_scan', 0.93, '#6366F1'),
          curatorCrop(P['red-bull'], 4, 'store-205', 'planogram_v3', 0.91, '#6366F1'),
          curatorCrop(P['red-bull'], 5, 'store-310', 'auto_detect_v2', 0.89, '#6366F1'),
          curatorCrop(P['red-bull'], 6, 'store-412', 'shelf_scan', 0.87, '#6366F1'),
          curatorCrop(P['monster'], 3, 'store-101', 'auto_detect_v2', 0.48, '#059669'),  // wrong — Monster in Sugar Free design
        ],
      },
      {
        id: '8b3f7d21-a946-4c1e-9e72-5d1f8a03',
        representativeImageUrl: productImageUrl(P['lucozade']),
        similarity: 0.71,
        cropCount: 12,
        barcode: '5010024009305',
        barcodeStatus: 'unconfirmed' as BarcodeStatus,
        sampleCrops: [
          curatorCrop(P['lucozade'], 0, 'store-205', 'shelf_scan', 0.95, '#D97706'),
          curatorCrop(P['lucozade'], 1, 'store-310', 'planogram_v3', 0.92, '#D97706'),
          curatorCrop(P['lucozade'], 2, 'store-101', 'auto_detect_v2', 0.88, '#D97706'),
          curatorCrop(P['red-bull'], 7, 'store-520', 'shelf_scan', 0.85, '#1D4ED8'),  // wrong — Red Bull in Lucozade design
        ],
      },
      {
        id: 'c7a42e59-1b83-4d6f-8024-9e6f3c71',
        representativeImageUrl: productImageUrl(P['monster']),
        similarity: 0.52,
        cropCount: 15,
        barcode: '5060166690199',
        barcodeStatus: 'pending' as BarcodeStatus,
        sampleCrops: [
          curatorCrop(P['monster'], 0, 'store-101', 'shelf_scan', 0.96, '#059669'),
          curatorCrop(P['monster'], 1, 'store-205', 'planogram_v3', 0.93, '#059669'),
          curatorCrop(P['monster'], 2, 'store-412', 'auto_detect_v2', 0.90, '#059669'),
          curatorCrop(P['monster-le'], 1, 'store-310', 'shelf_scan', 0.44, '#9333EA'),  // wrong — Monster LE in Monster design
        ],
      },
    ],
  };
}

// ===== Master Label UPC Data =====
// Intentionally messy data to demo fixing workflows:
// - Cola crops in Pepsi UPC, Pepsi crops in Cola UPC
// - Carton crop misplaced outside carton section
// - Inactive crops that need reactivation
// - Random trash crop to delete

const STORE_IDS = ['store-101', 'store-205', 'store-310', 'store-412', 'store-520', 'store-610', 'store-715'];
const SOURCES = ['planogram_v3', 'manual_capture', 'auto_detect_v2', 'shelf_scan', 'restock_photo'];

function masterCrop(product: MockProduct, idx: number, prefix: string, opts?: { inactive?: boolean; badImage?: boolean; mismatch?: boolean }) {
  const hash = (prefix.length * 7 + idx * 13) % STORE_IDS.length;
  return {
    id: `${prefix}-${idx}`,
    imageUrl: productCropUrl(product, idx),
    storeId: STORE_IDS[hash],
    source: SOURCES[(hash + idx) % SOURCES.length],
    ...(opts?.inactive ? { isInactive: true } : {}),
    ...(opts?.badImage ? { isBadImage: true } : {}),
    ...(opts?.mismatch ? { isMismatch: true } : {}),
  };
}

export const MOCK_MASTER_UPCS: MasterUPCData[] = [
  // ── Coca-Cola: 3 designs (2 confirmed first, 1 unconfirmed), Pepsi crops mixed in, carton misplaced ──
  {
    upc: P['coca-cola'].upc,
    productName: 'Coca-Cola 330ml',
    productImageUrl: productImageUrl(P['coca-cola']),
    itemNumber: 'ITM-00412',
    normalizedUpc: '5449000000996',
    alternativeImageUrls: [
      productImageUrl(P['coca-cola']),
      productImageUrl(P['coca-cola-promo']),
      productImageUrl(P['coca-cola-xmas']),
    ],
    designs: [
      // Confirmed designs come FIRST
      {
        id: 'd6bb0cd5-d73f-4481-bd6b-70af7ef8',
        representativeCropId: 'cc-front-0',
        barcode: '5449000000996',
        barcodeStatus: 'confirmed' as BarcodeStatus,
        frontImageUrl: 'https://placehold.co/200x200/DC2626/FFF?text=Coca-Cola+Verified',
        backImageUrl: 'https://placehold.co/200x200/991B1B/FFF?text=Coca-Cola+Back',
        crops: [
          masterCrop(P['coca-cola'], 0, 'cc-front'),
          masterCrop(P['coca-cola'], 1, 'cc-front'),
          masterCrop(P['coca-cola'], 2, 'cc-front'),
          masterCrop(P['pepsi'], 0, 'cc-wrong-pepsi'),        // WRONG: Pepsi crop in Cola design
          masterCrop(P['coca-cola'], 3, 'cc-front'),
          masterCrop(P['coca-cola'], 12, 'cc-front'),
          masterCrop(P['coca-cola'], 13, 'cc-front'),
          masterCrop(P['coca-cola'], 14, 'cc-front'),
          masterCrop(P['coca-cola'], 15, 'cc-front'),
          masterCrop(P['coca-cola'], 16, 'cc-front'),
          masterCrop(P['fanta'], 0, 'cc-wrong-fanta'),        // WRONG: Fanta crop mixed in
          masterCrop(P['coca-cola'], 17, 'cc-front'),
          masterCrop(P['coca-cola'], 18, 'cc-front'),
          masterCrop(P['coca-cola'], 19, 'cc-front'),
          masterCrop(P['coca-cola'], 20, 'cc-front'),
        ],
      },
      {
        id: 'cc-confirmed2-f8a1-4e72-b3d9-1c5e7a20',
        representativeCropId: 'cc-conf2-21',
        barcode: '5449000000996',
        barcodeStatus: 'confirmed' as BarcodeStatus,
        frontImageUrl: 'https://placehold.co/200x200/DC2626/FFF?text=Coca-Cola+Diet+Verified',
        backImageUrl: 'https://placehold.co/200x200/991B1B/FFF?text=Coca-Cola+Diet+Back',
        crops: [
          masterCrop(P['coca-cola'], 21, 'cc-conf2'),
          masterCrop(P['coca-cola-promo'], 22, 'cc-conf2', { mismatch: true }),
          masterCrop(P['coca-cola-promo'], 23, 'cc-conf2', { mismatch: true }),
          masterCrop(P['coca-cola-promo'], 24, 'cc-conf2', { mismatch: true }),
          masterCrop(P['coca-cola-promo'], 25, 'cc-conf2', { mismatch: true }),
          masterCrop(P['coca-cola-promo'], 26, 'cc-conf2', { mismatch: true }),
          masterCrop(P['sprite'], 0, 'cc-conf2-wrong-sprite'),  // WRONG: Sprite crop mixed in
          masterCrop(P['coca-cola-promo'], 27, 'cc-conf2', { mismatch: true }),
        ],
      },
      // Unconfirmed design comes AFTER confirmed
      {
        id: 'a3f21e8c-9b47-4d2a-8e15-c82f1a6d',
        representativeCropId: 'cc-alt-5',
        barcode: '5449000000996',
        barcodeStatus: 'unconfirmed' as BarcodeStatus,
        crops: [
          masterCrop(P['coca-cola'], 5, 'cc-alt'),
          masterCrop(P['coca-cola'], 6, 'cc-alt'),
          masterCrop(P['pepsi'], 2, 'cc-wrong-pepsi2'),       // WRONG: another Pepsi crop in Cola
          masterCrop(P['coca-cola'], 28, 'cc-alt'),
          masterCrop(P['coca-cola'], 29, 'cc-alt'),
          masterCrop(P['coca-cola'], 30, 'cc-alt'),
          masterCrop(P['coca-cola'], 31, 'cc-alt'),
          masterCrop(P['dr-pepper'], 0, 'cc-wrong-drp'),      // WRONG: Dr Pepper crop mixed in
          masterCrop(P['coca-cola'], 32, 'cc-alt'),
          masterCrop(P['coca-cola'], 33, 'cc-alt'),
        ],
      },
    ],
    unassignedCrops: [
      masterCrop(P['coca-cola-carton'], 0, 'cc-misplaced-carton'), // WRONG: carton crop not in carton section
      masterCrop(P['coca-cola'], 8, 'cc-unassigned'),
      masterCrop(P['coca-cola'], 9, 'cc-unassigned', { inactive: true }),  // INACTIVE: deactivated crop
    ],
    nonFrontCrops: [
      // Back images from the 2 confirmed designs (derived from backImageUrl)
      { id: 'cc-nf-back-v1', imageUrl: 'https://placehold.co/200x200/991B1B/FFF?text=Coca-Cola+Back', storeId: STORE_IDS[0], source: 'manual_capture' },
      { id: 'cc-nf-back-v2', imageUrl: 'https://placehold.co/200x200/991B1B/FFF?text=Coca-Cola+Diet+Back', storeId: STORE_IDS[1], source: 'manual_capture' },
      // Regular non-front crop with "x" placeholder
      masterCrop(P['coca-cola'], 10, 'cc-nf', { inactive: true }),
    ],
    cartonCrops: [
      masterCrop(P['coca-cola-carton'], 1, 'cc-carton'),
      masterCrop(P['coca-cola-carton'], 2, 'cc-carton'),
      masterCrop(P['coca-cola-carton'], 3, 'cc-carton'),
    ],
  },
  // ── Cola Zero: 2 designs, one confirmed ──
  {
    upc: P['cola-zero'].upc,
    productName: 'Cola Zero 330ml',
    productImageUrl: productImageUrl(P['cola-zero']),
    itemNumber: 'ITM-00418',
    normalizedUpc: '5449000131805',
    alternativeImageUrls: [
      productImageUrl(P['cola-zero']),
    ],
    designs: [
      {
        id: 'cz-a-9f12-4e85-8d37-6b1c0e53',
        representativeCropId: 'cz-a-0',
        barcode: '5449000131805',
        barcodeStatus: 'confirmed' as BarcodeStatus,
        frontImageUrl: productImageUrl(P['cola-zero'], 'Verified'),
        crops: [
          masterCrop(P['cola-zero'], 0, 'cz-a'),
          masterCrop(P['cola-zero'], 1, 'cz-a'),
          masterCrop(P['cola-zero'], 2, 'cz-a'),
          masterCrop(P['cola-zero'], 3, 'cz-a'),
          masterCrop(P['cola-zero'], 4, 'cz-a'),
          masterCrop(P['coca-cola'], 34, 'cz-wrong-cc'),     // WRONG: Coca-Cola crop in Zero design
          masterCrop(P['cola-zero'], 5, 'cz-a'),
          masterCrop(P['cola-zero'], 6, 'cz-a'),
          masterCrop(P['cola-zero'], 7, 'cz-a'),
          masterCrop(P['cola-zero'], 8, 'cz-a'),
          masterCrop(P['cola-zero'], 9, 'cz-a'),
          masterCrop(P['cola-zero'], 10, 'cz-a'),
        ],
      },
      {
        id: 'cz-b-2a85-7c31-4f96-d0b5a9e1',
        representativeCropId: 'cz-b-11',
        barcode: '5449000131805',
        barcodeStatus: 'unconfirmed' as BarcodeStatus,
        crops: [
          masterCrop(P['cola-zero'], 11, 'cz-b'),
          masterCrop(P['cola-zero'], 12, 'cz-b'),
          masterCrop(P['cola-zero'], 13, 'cz-b'),
          masterCrop(P['cola-zero'], 14, 'cz-b'),
          masterCrop(P['cola-zero'], 15, 'cz-b'),
          masterCrop(P['diet-coke'], 0, 'cz-wrong-dc'),     // WRONG: Diet Coke crop mixed in
          masterCrop(P['cola-zero'], 16, 'cz-b'),
        ],
      },
    ],
    unassignedCrops: [
      masterCrop(P['cola-zero'], 17, 'cz-unassigned'),
      masterCrop(P['cola-zero'], 18, 'cz-unassigned'),
    ],
    nonFrontCrops: [
      masterCrop(P['cola-zero'], 19, 'cz-nf'),
    ],
    cartonCrops: [
      masterCrop(P['cola-zero-carton'], 0, 'cz-carton'),
      masterCrop(P['cola-zero-carton'], 1, 'cz-carton'),
      masterCrop(P['cola-zero-carton'], 2, 'cz-carton'),
    ],
  },
  // ── Pepsi: has Cola crops mixed in, random trash crop, pending designs with 0-5 crops ──
  {
    upc: P['pepsi'].upc,
    productName: 'Pepsi 500ml',
    productImageUrl: productImageUrl(P['pepsi']),
    itemNumber: 'ITM-00789',
    normalizedUpc: '4060800100078',
    alternativeImageUrls: [
      productImageUrl(P['pepsi']),
      productImageUrl(P['pepsi-max']),
    ],
    designs: [
      {
        id: '7c4e9f12-2a85-41b7-9d63-f1e0b734',
        representativeCropId: 'pep-front-0',
        barcode: '4060800100078',
        barcodeStatus: 'pending' as BarcodeStatus,
        crops: [
          masterCrop(P['pepsi'], 0, 'pep-front'),
          masterCrop(P['coca-cola'], 4, 'pep-wrong-cola'),    // WRONG: Cola crop in Pepsi design
          masterCrop(P['pepsi'], 1, 'pep-front'),
          masterCrop(P['pepsi'], 3, 'pep-front'),
          masterCrop(P['pepsi'], 10, 'pep-front'),
        ],
      },
      {
        id: 'e8a52d91-6c03-48f5-b27d-9a4f3e17',
        representativeCropId: 'pep-b-4',
        barcode: '4060800100085',
        barcodeStatus: 'unconfirmed' as BarcodeStatus,
        crops: [
          masterCrop(P['pepsi'], 4, 'pep-b'),
          masterCrop(P['pepsi'], 5, 'pep-b'),
          masterCrop(P['pepsi'], 11, 'pep-b'),
          masterCrop(P['pepsi'], 12, 'pep-b'),
          masterCrop(P['pepsi'], 13, 'pep-b'),
          masterCrop(P['pepsi'], 14, 'pep-b'),
          masterCrop(P['pepsi'], 15, 'pep-b'),
          masterCrop(P['pepsi'], 16, 'pep-b'),
          masterCrop(P['monster'], 0, 'pep-wrong-mon'),       // WRONG: Monster crop in Pepsi design
          masterCrop(P['pepsi'], 17, 'pep-b'),
          masterCrop(P['pepsi'], 18, 'pep-b'),
          masterCrop(P['pepsi'], 19, 'pep-b'),
          masterCrop(P['pepsi'], 20, 'pep-b'),
        ],
      },
      {
        id: 'pep-c-3f91-4a7e-8d25-6b0c9e42',
        representativeCropId: 'pep-c-21',
        barcode: '4060800100092',
        barcodeStatus: 'pending' as BarcodeStatus,
        crops: [
          masterCrop(P['pepsi'], 21, 'pep-c'),
          masterCrop(P['pepsi'], 22, 'pep-c'),
          masterCrop(P['pepsi'], 23, 'pep-c'),
        ],
      },
    ],
    unassignedCrops: [
      masterCrop(P['trash-blurry'], 0, 'pep-trash', { badImage: true }),  // TRASH: random blurry shelf photo
      masterCrop(P['pepsi'], 6, 'pep-unassigned'),
      masterCrop(P['coca-cola'], 7, 'pep-wrong-cola2'),       // WRONG: another Cola crop in Pepsi
    ],
    nonFrontCrops: [
      masterCrop(P['pepsi'], 7, 'pep-nf', { inactive: true }),             // INACTIVE: deactivated non-front
      masterCrop(P['pepsi'], 24, 'pep-nf'),
    ],
    cartonCrops: [
      masterCrop(P['pepsi-carton'], 0, 'pep-carton'),
      masterCrop(P['pepsi-carton'], 1, 'pep-carton'),
      masterCrop(P['pepsi-carton'], 2, 'pep-carton'),
      masterCrop(P['pepsi-carton'], 3, 'pep-carton'),
      masterCrop(P['pepsi-carton'], 4, 'pep-carton'),
    ],
  },
  // ── Monster: three designs, expanded crops, Lucozade and Red Bull crops mixed in ──
  {
    upc: P['monster'].upc,
    productName: 'Monster Energy 500ml',
    productImageUrl: productImageUrl(P['monster']),
    itemNumber: 'ITM-01320',
    normalizedUpc: '5060166690199',
    alternativeImageUrls: [
      productImageUrl(P['monster']),
      productImageUrl(P['monster-le']),
    ],
    designs: [
      {
        id: '91c7e3a4-5f28-4b96-82d1-6a0f9c53',
        representativeCropId: 'mon-a-0',
        barcode: '5060166690199',
        barcodeStatus: 'unconfirmed' as BarcodeStatus,
        crops: [
          masterCrop(P['monster'], 0, 'mon-a'),
          masterCrop(P['monster'], 1, 'mon-a'),
          masterCrop(P['monster'], 2, 'mon-a'),
          masterCrop(P['monster'], 3, 'mon-a'),
          masterCrop(P['monster'], 4, 'mon-a'),
          masterCrop(P['monster'], 6, 'mon-a'),
          masterCrop(P['monster'], 7, 'mon-a'),
          masterCrop(P['monster'], 8, 'mon-a'),
          masterCrop(P['lucozade'], 1, 'mon-wrong-luc'),      // WRONG: Lucozade crop
          masterCrop(P['monster'], 9, 'mon-a'),
          masterCrop(P['monster'], 10, 'mon-a'),
          masterCrop(P['monster'], 11, 'mon-a'),
          masterCrop(P['monster'], 12, 'mon-a'),
          masterCrop(P['red-bull'], 0, 'mon-wrong-rb'),       // WRONG: Red Bull crop
          masterCrop(P['monster'], 13, 'mon-a'),
        ],
      },
      {
        id: 'f4d82b15-3e69-47ca-a918-c7210e86',
        representativeCropId: 'mon-b-0',
        barcode: '5060166690206',
        barcodeStatus: 'unconfirmed' as BarcodeStatus,
        crops: [
          masterCrop(P['monster-le'], 0, 'mon-b'),
          masterCrop(P['monster-le'], 1, 'mon-b'),
          masterCrop(P['monster-le'], 2, 'mon-b'),
          masterCrop(P['monster-le'], 3, 'mon-b'),
          masterCrop(P['monster-le'], 4, 'mon-b'),
          masterCrop(P['monster-le'], 5, 'mon-b'),
          masterCrop(P['monster-le'], 6, 'mon-b'),
          masterCrop(P['monster-le'], 7, 'mon-b'),
          masterCrop(P['monster-le'], 8, 'mon-b'),
          masterCrop(P['monster-le'], 9, 'mon-b'),
        ],
      },
      {
        id: 'mon-c-7a3f-9e12-4b85-d6c0e1f2',
        representativeCropId: 'mon-c-14',
        barcode: '5060166690213',
        barcodeStatus: 'pending' as BarcodeStatus,
        crops: [
          masterCrop(P['monster'], 14, 'mon-c'),
          masterCrop(P['monster'], 15, 'mon-c'),
          masterCrop(P['monster'], 16, 'mon-c'),
          masterCrop(P['monster'], 17, 'mon-c'),
          masterCrop(P['monster'], 18, 'mon-c'),
        ],
      },
    ],
    unassignedCrops: [
      masterCrop(P['monster'], 19, 'mon-unassigned'),
      masterCrop(P['monster'], 20, 'mon-unassigned'),
    ],
    nonFrontCrops: [
      masterCrop(P['monster'], 5, 'mon-nf'),
      masterCrop(P['monster'], 21, 'mon-nf'),
    ],
    cartonCrops: [
      masterCrop(P['monster'], 22, 'mon-carton'),
      masterCrop(P['monster'], 23, 'mon-carton'),
      masterCrop(P['monster'], 24, 'mon-carton'),
    ],
  },
  // ── Sprite: two designs, expanded crops ──
  {
    upc: P['sprite'].upc,
    productName: 'Sprite 330ml',
    productImageUrl: productImageUrl(P['sprite']),
    itemNumber: 'ITM-00455',
    normalizedUpc: '5449000014535',
    alternativeImageUrls: [
      productImageUrl(P['sprite']),
      productImageUrl(P['sprite-zero']),
    ],
    designs: [
      {
        id: '5a3d8f72-c194-4e6b-b037-2e91f4a8',
        representativeCropId: 'spr-front-0',
        barcode: '5449000014535',
        barcodeStatus: 'confirmed' as BarcodeStatus,
        frontImageUrl: 'https://placehold.co/200x200/008B45/FFF?text=Sprite+Verified',
        crops: [
          masterCrop(P['sprite'], 0, 'spr-front'),
          masterCrop(P['sprite'], 1, 'spr-front'),
          masterCrop(P['sprite'], 2, 'spr-front'),
          masterCrop(P['sprite'], 3, 'spr-front'),
          masterCrop(P['sprite'], 6, 'spr-front'),
          masterCrop(P['sprite'], 7, 'spr-front'),
          masterCrop(P['sprite'], 8, 'spr-front'),
          masterCrop(P['sprite'], 9, 'spr-front'),
          masterCrop(P['sprite'], 10, 'spr-front'),
          masterCrop(P['sprite'], 11, 'spr-front'),
          masterCrop(P['fanta-lemon'], 0, 'spr-wrong-fl'),    // WRONG: Fanta Lemon crop mixed in
          masterCrop(P['sprite'], 12, 'spr-front'),
          masterCrop(P['sprite'], 13, 'spr-front'),
        ],
      },
      {
        id: 'spr-b-2e94-7c31-4f86-a0d5b9e8',
        representativeCropId: 'spr-b-0',
        barcode: '5449000014542',
        barcodeStatus: 'unconfirmed' as BarcodeStatus,
        crops: [
          masterCrop(P['sprite-zero'], 0, 'spr-b'),
          masterCrop(P['sprite-zero'], 1, 'spr-b'),
          masterCrop(P['sprite-zero'], 2, 'spr-b'),
          masterCrop(P['sprite-zero'], 3, 'spr-b'),
          masterCrop(P['sprite-zero'], 4, 'spr-b'),
          masterCrop(P['sprite-zero'], 5, 'spr-b'),
          masterCrop(P['sprite-zero'], 6, 'spr-b'),
        ],
      },
    ],
    unassignedCrops: [
      masterCrop(P['sprite'], 4, 'spr-unassigned'),
      masterCrop(P['sprite'], 5, 'spr-unassigned'),
      masterCrop(P['sprite'], 14, 'spr-unassigned'),
    ],
    nonFrontCrops: [
      masterCrop(P['sprite'], 15, 'spr-nf'),
    ],
    cartonCrops: [
      masterCrop(P['sprite'], 16, 'spr-carton'),
      masterCrop(P['sprite'], 17, 'spr-carton'),
      masterCrop(P['sprite'], 18, 'spr-carton'),
      masterCrop(P['sprite'], 19, 'spr-carton'),
    ],
  },
  // ── Fanta: two designs, expanded crops, pending design with few crops ──
  {
    upc: P['fanta'].upc,
    productName: 'Fanta Orange 330ml',
    productImageUrl: productImageUrl(P['fanta']),
    itemNumber: 'ITM-00467',
    normalizedUpc: '5449000011527',
    alternativeImageUrls: [
      productImageUrl(P['fanta']),
      productImageUrl(P['fanta-lemon']),
    ],
    designs: [
      {
        id: 'b19e6c43-8d72-45a1-9f28-3c7a5d06',
        representativeCropId: 'fan-front-0',
        barcode: '5449000011527',
        barcodeStatus: 'pending' as BarcodeStatus,
        crops: [
          masterCrop(P['fanta'], 0, 'fan-front'),
          masterCrop(P['fanta'], 1, 'fan-front'),
          masterCrop(P['fanta'], 2, 'fan-front'),
          masterCrop(P['fanta'], 3, 'fan-front'),
          masterCrop(P['fanta'], 4, 'fan-front'),
          masterCrop(P['fanta'], 5, 'fan-front'),
          masterCrop(P['fanta'], 6, 'fan-front'),
          masterCrop(P['fanta'], 8, 'fan-front'),
          masterCrop(P['fanta'], 9, 'fan-front'),
          masterCrop(P['fanta'], 10, 'fan-front'),
          masterCrop(P['fanta'], 11, 'fan-front'),
          masterCrop(P['fanta'], 12, 'fan-front'),
        ],
      },
      {
        id: 'fan-b-5c18-3d92-4a7f-e0b6f8d1',
        representativeCropId: 'fan-b-1',
        barcode: '5449000011534',
        barcodeStatus: 'unconfirmed' as BarcodeStatus,
        crops: [
          masterCrop(P['fanta-lemon'], 1, 'fan-b'),
          masterCrop(P['fanta-lemon'], 2, 'fan-b'),
          masterCrop(P['fanta-lemon'], 3, 'fan-b'),
          masterCrop(P['fanta-lemon'], 4, 'fan-b'),
          masterCrop(P['fanta-lemon'], 5, 'fan-b'),
          masterCrop(P['fanta-lemon'], 6, 'fan-b'),
          masterCrop(P['fanta-lemon'], 7, 'fan-b'),
          masterCrop(P['fanta-lemon'], 8, 'fan-b'),
          masterCrop(P['sprite'], 0, 'fan-wrong-sprite'),     // WRONG: Sprite crop mixed in
        ],
      },
    ],
    unassignedCrops: [
      masterCrop(P['fanta'], 13, 'fan-unassigned'),
      masterCrop(P['fanta'], 14, 'fan-unassigned'),
    ],
    nonFrontCrops: [
      masterCrop(P['fanta'], 15, 'fan-nf'),
      masterCrop(P['fanta'], 16, 'fan-nf', { inactive: true }),
    ],
    cartonCrops: [
      masterCrop(P['fanta'], 7, 'fan-carton'),
      masterCrop(P['fanta'], 17, 'fan-carton'),
      masterCrop(P['fanta'], 18, 'fan-carton'),
      masterCrop(P['fanta'], 19, 'fan-carton'),
      masterCrop(P['fanta'], 20, 'fan-carton'),
    ],
  },
];

// Design Review — cross-design cleanup highlights using real UPC data
function designReviewPayload(): ForgeDesignReviewPayload {
  // Pick Coca-Cola, Cola Zero, and Pepsi UPCs from master data
  const upcs = [MOCK_MASTER_UPCS[0], MOCK_MASTER_UPCS[1], MOCK_MASTER_UPCS[2]];

  // Ordered: similar → outliers → non-front → carton → pending → assign → anything else
  const highlights: ReviewHighlight[] = [
    // similar_crops #1: Pepsi crops in Cola designs look like Pepsi crops in Pepsi UPC
    {
      id: 'rh-001',
      type: 'similar_crops',
      cropIds: ['cc-wrong-pepsi-0', 'cc-wrong-pepsi2-2', 'pep-b-4', 'pep-b-11', 'pep-b-16', 'pep-b-17', 'pep-b-19'],
      similarity: 0.92,
    },
    // similar_crops #2: standard Coca-Cola crops across designs 1 and 3 that all look the same
    {
      id: 'rh-008',
      type: 'similar_crops',
      cropIds: [
        'cc-front-1', 'cc-front-2', 'cc-front-3', 'cc-front-12', 'cc-front-13', 'cc-front-14',
        'cc-front-15', 'cc-front-16', 'cc-front-17', 'cc-front-18', 'cc-front-19', 'cc-front-20',
        'cc-alt-6', 'cc-alt-28', 'cc-alt-29', 'cc-alt-30', 'cc-alt-31', 'cc-alt-32', 'cc-alt-33',
      ],
      similarity: 0.91,
    },
    // outlier_crops: wrong brand crops across the 3 UPCs
    {
      id: 'rh-002',
      type: 'outlier_crops',
      cropIds: [
        'cc-wrong-fanta-0',           // Fanta in Cola main
        'cc-conf2-wrong-sprite-0',    // Sprite in Cola confirmed-2
        'cc-wrong-drp-0',             // Dr Pepper in Cola alt
        'pep-wrong-cola-4',           // Cola in Pepsi main
        'pep-wrong-mon-0',            // Monster in Pepsi design b
        'pep-wrong-cola2-7',          // Cola in Pepsi unassigned
        'cz-wrong-cc-34',             // Coca-Cola in Cola Zero
        'cz-wrong-dc-0',              // Diet Coke in Cola Zero
      ],
      confidence: 0.94,
    },
    // should_be_non_front: Coca-Cola crop in alt design that looks like a side/back view
    {
      id: 'rh-003',
      type: 'should_be_non_front',
      cropId: 'cc-alt-6',
      currentDesignId: 'a3f21e8c-9b47-4d2a-8e15-c82f1a6d',
      confidence: 0.85,
    },
    // should_be_carton: carton crop sitting in Coca-Cola unassigned instead of carton section
    {
      id: 'rh-004',
      type: 'should_be_carton',
      cropId: 'cc-misplaced-carton-0',
      currentDesignId: undefined,
      confidence: 0.96,
    },
    // clear_pending: all pending crops across all UPCs (section-level, one per task)
    {
      id: 'rh-005',
      type: 'clear_pending',
    },
    // assign_design: all unassigned crops across all UPCs (section-level, one per task)
    {
      id: 'rh-006',
      type: 'assign_design',
    },
    // anything_else: freeform final review step
    {
      id: 'rh-007',
      type: 'anything_else',
    },
  ];

  return { upcs, highlights };
}

// Uncertain Classification — crops with low recognition scores (70-85% range)
function uncertainClassificationPayload(): ForgeUncertainClassificationPayload {
  const crops: UncertainCrop[] = [
    {
      id: 'uc-crop-001',
      imageUrl: productCropUrl(P['pepsi'], 0),
      upc: P['pepsi'].upc,
      productName: 'Pepsi 500ml',
      recognitionScore: 0.72,
      designColor: '#1E40AF',
      predictions: [
        { designId: 'var-pepsi-01', designName: 'Pepsi 500ml Regular', representativeImageUrl: productImageUrl(P['pepsi']), confidence: 0.84 },
        { designId: 'var-pepsi-max-01', designName: 'Pepsi Max 500ml', representativeImageUrl: productImageUrl(P['pepsi-max']), confidence: 0.76 },
        { designId: 'var-cola-01', designName: 'Coca-Cola 330ml', representativeImageUrl: productImageUrl(P['coca-cola']), confidence: 0.70 },
      ],
    },
    {
      id: 'uc-crop-002',
      imageUrl: productCropUrl(P['coca-cola'], 3),
      upc: P['coca-cola'].upc,
      productName: 'Coca-Cola 330ml',
      recognitionScore: 0.70,
      designColor: '#DC2626',
      predictions: [
        { designId: 'var-cola-01', designName: 'Coca-Cola 330ml', representativeImageUrl: productImageUrl(P['coca-cola']), confidence: 0.82 },
        { designId: 'var-cola-cherry-01', designName: 'Cola Cherry 330ml', representativeImageUrl: productImageUrl(P['cola-cherry']), confidence: 0.74 },
        { designId: 'var-cola-zero-01', designName: 'Cola Zero 330ml', representativeImageUrl: productImageUrl(P['cola-zero']), confidence: 0.70 },
      ],
    },
    {
      id: 'uc-crop-003',
      imageUrl: productCropUrl(P['sprite'], 2),
      upc: P['sprite'].upc,
      productName: 'Sprite 330ml',
      recognitionScore: 0.78,
      designColor: '#16A34A',
      predictions: [
        { designId: 'var-sprite-01', designName: 'Sprite 330ml', representativeImageUrl: productImageUrl(P['sprite']), confidence: 0.84 },
        { designId: 'var-sprite-zero-01', designName: 'Sprite Zero 330ml', representativeImageUrl: productImageUrl(P['sprite-zero']), confidence: 0.78 },
        { designId: 'var-fanta-lemon-01', designName: 'Fanta Lemon 330ml', representativeImageUrl: productImageUrl(P['fanta-lemon']), confidence: 0.71 },
      ],
    },
    {
      id: 'uc-crop-004',
      imageUrl: productCropUrl(P['monster'], 2),
      upc: P['monster'].upc,
      productName: 'Monster Energy 500ml',
      recognitionScore: 0.73,
      designColor: '#052E16',
      predictions: [
        { designId: 'var-monster-01', designName: 'Monster Energy Original', representativeImageUrl: productImageUrl(P['monster']), confidence: 0.81 },
        { designId: 'var-monster-le-01', designName: 'Monster LE', representativeImageUrl: productImageUrl(P['monster-le']), confidence: 0.77 },
        { designId: 'var-lucozade-01', designName: 'Lucozade Energy', representativeImageUrl: productImageUrl(P['lucozade']), confidence: 0.72 },
      ],
    },
    {
      id: 'uc-crop-005',
      imageUrl: productCropUrl(P['fanta'], 4),
      upc: P['fanta'].upc,
      productName: 'Fanta Orange 330ml',
      recognitionScore: 0.81,
      designColor: '#EA580C',
      predictions: [
        { designId: 'var-fanta-01', designName: 'Fanta Orange 330ml', representativeImageUrl: productImageUrl(P['fanta']), confidence: 0.85 },
        { designId: 'var-fanta-lemon-01', designName: 'Fanta Lemon 330ml', representativeImageUrl: productImageUrl(P['fanta-lemon']), confidence: 0.78 },
        { designId: 'var-lucozade-01', designName: 'Lucozade Energy', representativeImageUrl: productImageUrl(P['lucozade']), confidence: 0.72 },
      ],
    },
    {
      id: 'uc-crop-006',
      imageUrl: productCropUrl(P['red-bull'], 5),
      upc: P['red-bull'].upc,
      productName: 'Red Bull 250ml',
      recognitionScore: 0.75,
      designColor: '#1D4ED8',
      predictions: [
        { designId: 'var-redbull-01', designName: 'Red Bull Original 250ml', representativeImageUrl: productImageUrl(P['red-bull']), confidence: 0.83 },
        { designId: 'var-monster-01', designName: 'Monster Energy Original', representativeImageUrl: productImageUrl(P['monster']), confidence: 0.75 },
        { designId: 'var-lucozade-01', designName: 'Lucozade Energy', representativeImageUrl: productImageUrl(P['lucozade']), confidence: 0.70 },
      ],
    },
    {
      id: 'uc-crop-007',
      imageUrl: productCropUrl(P['dr-pepper'], 1),
      upc: P['dr-pepper'].upc,
      productName: 'Dr Pepper 330ml',
      recognitionScore: 0.74,
      designColor: '#7F1D1D',
      predictions: [
        { designId: 'var-drpepper-01', designName: 'Dr Pepper 330ml', representativeImageUrl: productImageUrl(P['dr-pepper']), confidence: 0.82 },
        { designId: 'var-cola-cherry-01', designName: 'Cola Cherry 330ml', representativeImageUrl: productImageUrl(P['cola-cherry']), confidence: 0.76 },
        { designId: 'var-cola-01', designName: 'Coca-Cola 330ml', representativeImageUrl: productImageUrl(P['coca-cola']), confidence: 0.71 },
      ],
    },
    {
      id: 'uc-crop-008',
      imageUrl: productCropUrl(P['lucozade'], 1),
      upc: P['lucozade'].upc,
      productName: 'Lucozade Energy',
      recognitionScore: 0.71,
      designColor: '#D97706',
      predictions: [
        { designId: 'var-lucozade-01', designName: 'Lucozade Energy', representativeImageUrl: productImageUrl(P['lucozade']), confidence: 0.80 },
        { designId: 'var-fanta-01', designName: 'Fanta Orange 330ml', representativeImageUrl: productImageUrl(P['fanta']), confidence: 0.74 },
        { designId: 'var-monster-01', designName: 'Monster Energy Original', representativeImageUrl: productImageUrl(P['monster']), confidence: 0.70 },
      ],
    },
  ];

  return {
    threshold: 0.85,
    crops,
    candidateDesigns: [
      {
        id: 'var-pepsi-01',
        upc: P['pepsi'].upc,
        representativeImageUrl: productImageUrl(P['pepsi']),
        similarity: 0.92,
        cropCount: 22,
        barcodeStatus: 'confirmed' as BarcodeStatus,
        sampleCrops: makeSamples('pepsi', 3),
      },
      {
        id: 'var-cola-01',
        upc: P['coca-cola'].upc,
        representativeImageUrl: productImageUrl(P['coca-cola']),
        similarity: 0.89,
        cropCount: 35,
        barcodeStatus: 'confirmed' as BarcodeStatus,
        sampleCrops: makeSamples('coca-cola', 3),
      },
      {
        id: 'var-sprite-01',
        upc: P['sprite'].upc,
        representativeImageUrl: productImageUrl(P['sprite']),
        similarity: 0.87,
        cropCount: 18,
        barcodeStatus: 'confirmed' as BarcodeStatus,
        sampleCrops: makeSamples('sprite', 3),
      },
      {
        id: 'var-monster-01',
        upc: P['monster'].upc,
        representativeImageUrl: productImageUrl(P['monster']),
        similarity: 0.83,
        cropCount: 28,
        barcodeStatus: 'unconfirmed' as BarcodeStatus,
        sampleCrops: makeSamples('monster', 3),
      },
      {
        id: 'var-redbull-01',
        upc: P['red-bull'].upc,
        representativeImageUrl: productImageUrl(P['red-bull']),
        similarity: 0.91,
        cropCount: 15,
        barcodeStatus: 'confirmed' as BarcodeStatus,
        sampleCrops: makeSamples('red-bull', 3),
      },
    ],
  };
}

export const MOCK_TASKS: ForgeTask[] = [
  // 1. Design Review
  {
    id: 'task-008',
    type: 'DESIGN_REVIEW',
    queueId: 'design_review',
    queueName: 'design_review',
    chainId: 1,
    chainName: 'morrisons',
    priority: 'normal',
    payload: designReviewPayload(),
  },
  // 2. Design Curator
  {
    id: 'task-001',
    type: 'DESIGN_CURATOR' as const,
    queueId: 'design_curator',
    queueName: 'design_curator',
    chainId: 3,
    chainName: 'lidl_gb',
    priority: 'high',
    payload: designCuratorPayload(),
  },
  // 3. Uncertain Classification
  {
    id: 'task-010',
    type: 'UNCERTAIN_CLASSIFICATION',
    queueId: 'uncertain_classification',
    queueName: 'uncertain_classification',
    chainId: 3,
    chainName: 'lidl_gb',
    priority: 'normal',
    payload: uncertainClassificationPayload(),
  },
  // 4. FD Classification
  {
    id: 'task-007',
    type: 'FD_CLASSIFICATION',
    queueId: 'fd_classification',
    queueName: 'fd_classification',
    chainId: 1,
    chainName: 'morrisons',
    priority: 'high',
    payload: fdClassificationPayload(),
  },
];

// ===== Labeler Management =====
export const MOCK_LABELERS: ForgeLabeler[] = [
  { id: 'l1', name: 'cogito_1', email: 'cogito_1@cogito.com', role: 'Admin', assignedQueues: ['fd_classification', 'design_curator', 'design_review', 'uncertain_classification'] },
  { id: 'l2', name: 'cogito_2', email: 'cogito_2@cogito.com', role: 'Labeler', assignedQueues: ['fd_classification', 'design_review', 'uncertain_classification'] },
  { id: 'l3', name: 'cogito_3', email: 'cogito_3@cogito.com', role: 'Labeler', assignedQueues: ['design_review', 'design_curator', 'uncertain_classification'] },
  { id: 'l4', name: 'cogito_4', email: 'cogito_4@cogito.com', role: 'Labeler', assignedQueues: ['design_review', 'design_curator'] },
  { id: 'l5', name: 'cogito_5', email: 'cogito_5@cogito.com', role: 'Labeler', assignedQueues: ['design_review', 'design_curator'] },
];
